import { Payment } from 'payments-lib-types';
import type { User } from '@/api/auth';
import { playerPaymentsWithPages } from '@/api/profile';
import { isProduction } from '@/helpers/common';
import { createWaitForGlobal } from '@/helpers/waitForGlobal';
import { useTrackingUserParamsStore } from '@/modules/userTrackingParams/store/useTrackingUserStore';

declare global {
  interface Window {
    hj?: (...args: any[]) => void;
  }
}

// DEV-1555: Hotjar records a session once this custom event fires. It is added
// as a recording-targeting condition in the Hotjar dashboard (site 2010216).
export const HOTJAR_STAG_EVENT_NAME = 'hj_event_stag_present';

// stag is stored as `affiliateID_visitorID` (e.g. `149232_abc`); we target the
// affiliate-ID segment only (`stag.split('_')[0]`).
export const TARGET_STAG_AFFILIATE_ID = '149232';

// Records sessions only for users whose first deposit is AFTER 2026-06-01.
// `to` is the last day strictly BEFORE the cutoff, so an FTD of exactly
// 2026-06-01 is treated as "after".
const FTD_CUTOFF_TO = '2026-05-31';

const waitForHotjar = createWaitForGlobal(
  () => (typeof window.hj === 'function' ? window.hj : null),
  { name: 'Hotjar' }
);

const fireHotjarEvent = (eventName: string) => {
  waitForHotjar()
    .then((hj) => {
      try {
        // TODO: remove — local debug for DEV-1555
        console.log('Hotjar event fired:', eventName);
        hj('event', eventName);
      } catch {
        console.error('Hotjar trackEvent failed');
      }
    })
    .catch(() => {});
};

// Hotjar is only loaded in production; no-op (with a dev log) elsewhere.
export const trackHotjarEvent: (eventName: string) => void = isProduction()
  ? fireHotjarEvent
  : (eventName) => {
      console.log('DEV MODE: hotjar event should fire', eventName);
    };

export const getTrackedStag = (): string | null =>
  useTrackingUserParamsStore.getState().stag ?? null;

export const isTargetStag = (stag = getTrackedStag()): boolean =>
  (stag?.split('_')[0] ?? '') === TARGET_STAG_AFFILIATE_ID;

/**
 * Caller must have already confirmed the user is a depositor (not in group
 * 00002). Returns `true` if the user has a succeeded deposit BEFORE the cutoff,
 * i.e. their first deposit is NOT after 2026-06-01.
 *
 * NOTE: scoped to `user.currency` — a pre-cutoff deposit in another currency
 * would be missed (acceptable / near-zero for this cohort).
 */
export const hasSucceededDepositBeforeCutoff = async (
  user: User
): Promise<boolean> => {
  const page = await playerPaymentsWithPages({
    page: 1,
    pageSize: 20,
    filter: {
      currency: user.currency,
      type: Payment.Action.Deposit,
      to: FTD_CUTOFF_TO,
    },
  });

  return (page?.data ?? []).some(
    (transaction) => transaction.state === 'succeeded' || transaction.success
  );
};
